import React, { useContext } from 'react';

import { permissionNames } from 'common/bae/constants';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import VStack from 'common/ui/VStack';
import Dialog from 'common/uiV3/Dialog';
import UserLockupV2 from 'common/user/UserLockupV2';
import getFirstName from 'common/util/getFirstName';
import getMembersWithPermission from 'common/util/getMembersWithPermission';
import queryString from 'common/util/queryString';

import type { RolePermissionName } from 'common/api/endpoints/companies';

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  requiredPermissions: RolePermissionName[];
};

const AccessModalV2: React.FC<Props> = ({ isOpen, onOpenChange, requiredPermissions }: Props) => {
  const company = useContext(CompanyContext);
  const viewer = useContext(ViewerContext);

  const recipients = getMembersWithPermission('manageTeammates', company);
  const rolesWithPermissions = company.roles.filter((role) =>
    requiredPermissions.every((permKey) => role.permissions[permKey]),
  );
  const permissions = requiredPermissions.map((permKey) => permissionNames[permKey]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className="gap-0 sm:max-w-[450px]">
        <Dialog.Header>
          <VStack className="max-w-[300px] gap-2 text-center mx-auto">
            <Dialog.Title className="font-semibold">
              You do not have access to this feature
            </Dialog.Title>
            <Dialog.Description>
              Please ask one of your teammates with admin access to give you permission.
            </Dialog.Description>
          </VStack>
        </Dialog.Header>
        <VStack className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 max-h-[250px] overflow-y-auto">
          {recipients.map((recipient) => {
            const link =
              'mailto:' +
              recipient.email +
              queryString.stringify({
                subject: 'Canny - Requesting Additional Permissions',
                body:
                  'Hi ' +
                  getFirstName(recipient.name) +
                  ',\n' +
                  '\n' +
                  "I'd like to request additional permissions in Canny. Specifically, I would like to be able to use the following permission: " +
                  permissions.join(', ') +
                  '\n' +
                  '\n' +
                  'This would involve changing my role to one of the following roles: ' +
                  rolesWithPermissions.map((role) => role.name).join(', ') +
                  '. Alternatively, you could add the permission to my current role or create a new role entirely.' +
                  '\n' +
                  '\n' +
                  'You can make these changes here: https://' +
                  company.subdomain +
                  '.canny.io/admin/settings/team\n' +
                  '\n' +
                  'Thanks,\n' +
                  getFirstName(viewer.name),
              });

            return (
              <a key={recipient._id} href={link} rel="noopener" target="_blank" className="block">
                <UserLockupV2
                  showBadge={false}
                  showCompanyNames={false}
                  showProfile={false}
                  showTint={false}
                  user={recipient}
                />
              </a>
            );
          })}
        </VStack>
      </Dialog.Content>
    </Dialog>
  );
};

export default AccessModalV2;
